import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import portfolioService from '../../services/portfolioService';
import PortfolioCard from './PortfolioCard';
import './PortfolioList.css';
import { FaIndianRupeeSign } from 'react-icons/fa6';
import CommonModal from '../../services/CommonModal';
import PortfolioListView from './PortfolioListView';
import { FaFileExcel } from 'react-icons/fa';

const PortfolioList = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'tree'

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    is_active: '',
    owner_id: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalBudget: 0,
    totalSpent: 0,
    totalProjects: 0,
    totalbudgetspent: 0
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    fetchPortfolios();
  }, [filters]);

  const fetchPortfolios = async () => {
    try {
      setLoading(true);
      const data = await portfolioService.getAllPortfolios({
        ...filters,
        include_stats: 'true'
      });

      setPortfolios(data.portfolios || []);

      if (data.stats) {
        setStats({
          total: data.stats.total_portfolios || 0,
          active: data.stats.active_portfolios || 0,
          totalBudget: data.stats.total_budget_allocated || 0,
          totalSpent: data.stats.total_budget_spent || 0,
          totalProjects: data.stats.total_projects || 0,
          totalbudgetspent: data.stats.total_budget_spent || 0,
        });
      }

      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch portfolios');
      setPortfolios([]);
    } finally {
      setLoading(false);
    }
  };


  const confirmDelete = () => {
    if (selectedId) {
      handleDelete(selectedId); // your existing delete API/function
    }

    setShowDeleteModal(false);
    setSelectedId(null);
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };



  const handleDelete = async (id) => {
    // if (!window.confirm('Are you sure you want to delete this portfolio?')) {
    //   return;
    // }

    try {
      await portfolioService.deletePortfolio(id);
      fetchPortfolios();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete portfolio');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const budgetUtilization = stats.totalBudget > 0
    ? ((stats.totalSpent / stats.totalBudget) * 100).toFixed(2)
    : 0;

  const handleDownloadPortfolioList = () => {
    if (!portfolios || portfolios.length === 0) {
      alert('No portfolios to download');
      return;
    }

    const headers = [
      'Name', 'Status', 'Active', 'Total Projects',
      'Budget Allocated', 'Budget Spent'
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = portfolios.map((portfolio) => [
      portfolio.name,
      portfolio.status,
      portfolio.is_active ? 'Active' : 'Inactive',
      portfolio.total_projects ?? portfolio.projects?.length ?? 0,
      portfolio.budget_allocated ?? '',
      portfolio.budget_spent ?? ''
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `portfolios-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="portfolio-list-container">
      <div className="portfolio-list-header">
        <h1>Portfolios</h1>
        <div className="flex gap-2">
          <Link to="/app/portfolios/new" className="btn btn-primary">
            <i className="fas fa-plus"></i> Create Portfolio
          </Link>
          <button onClick={handleDownloadPortfolioList} className="px-4 py-2 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2" title="Download portfolio list">
            <FaFileExcel />
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card portfolio-direction" >
          <div className="stat-icon bg-[#a4d3f5!important] shadow-lg">
            <i className="fas fa-briefcase text-[white]"></i>
          </div>
          <div className="stat-content">
            <div className="stat-value no-background ">{stats.total}</div>
            <div className="stat-label">Total Portfolios</div>
          </div>
        </div>

        <div className="stat-card portfolio-direction" >
          <div className="stat-icon bg-[#a0f8a7!important] shadow-lg">
            <i className="fas fa-check-circle text-[white]"></i>
          </div>
          <div className="stat-content">
            <div className="stat-value no-background">{stats.active}</div>
            <div className="stat-label">Active Portfolios</div>
          </div>
        </div>

        <div className="stat-card portfolio-direction" >
          <div className="stat-icon bg-[#f6b9ff!important] shadow-lg">
            <i className="fas fa-project-diagram text-[white]"></i>
          </div>
          <div className="stat-content">
            <div className="stat-value no-background ">{stats.totalProjects}</div>
            <div className="stat-label">Total Projects</div>
          </div>
        </div>

        <div className="stat-card portfolio-direction" >
          <div className="stat-icon bg-[#fddfaf!important] shadow-lg">
            <FaIndianRupeeSign className='text-[white]'/>
          </div>
          <div className="stat-content">
            <div className="stat-value no-background">
              {portfolioService.formatBudget(stats.totalBudget)}
            </div>
            <div className="stat-label">Total Budget</div>
          </div>
        </div>

        <div className="stat-card portfolio-direction" >
          <div className="stat-icon bg-[#fcd79d!important] shadow-lg">
            <FaIndianRupeeSign className='text-[white]'/>
          </div>
          <div className="stat-content">
            <div className="stat-value no-background">
              {portfolioService.formatBudget(stats.totalbudgetspent)}
            </div>
            <div className="stat-label">Total Budget Spent</div>
          </div>
        </div>

        <div className="stat-card portfolio-direction" >
          <div className="stat-icon bg-[#ffcdde!important] shadow-lg">
            <i className="fas fa-chart-line text-[white]"></i>
          </div>
          <div className="stat-content">
            <div className="stat-value no-background">{budgetUtilization}%</div>
            <div className="stat-label">Budget Utilization</div>
          </div>
        </div>
      </div>

      {/* Filters */}       
      <div className="grid md:grid-cols-3 bg-white p-4 mb-6 rounded-lg shadow-sm gap-4">
        <div className="">
          <input
            type="text"
            name="search"
            className="form-control"
            placeholder="Search portfolios..."
            value={filters.search}
            onChange={handleFilterChange}
          />
        </div>

        <div className=" ">
          <select
            name="status"
            className="form-control"
            value={filters.status}
            onChange={handleFilterChange}
          >
            <option value="">All Status</option>
            <option value="Planning">Planning</option>
            <option value="Active">Active</option>
            <option value="On Hold">On Hold</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>

        <div className=" ">
          <select
            name="is_active"
            className="form-control"
            value={filters.is_active}
            onChange={handleFilterChange}
          >
            <option value="">All Active Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>
      
      <div className="view-toggle mb-4">
        <button
          className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
          onClick={() => setViewMode('grid')}
        >
          <i className="fas fa-th"></i> Grid View
        </button>
        <button
          className={`toggle-btn ${viewMode === 'tree' ? 'active' : ''}`}
          onClick={() => setViewMode('tree')}
        >
          <i className="fas fa-sitemap"></i> List View
        </button>
      </div>


      {loading && portfolios.length === 0 ? (
        <div className="loading">Loading portfolios...</div>
      ) : portfolios.length === 0 ? (
        <div className="no-portfolios">
          <i className="fas fa-briefcase"></i>
          <p>No portfolios found</p>
          <Link to="/app/portfolios/new" className="btn btn-primary">
            Create Your First Portfolio
          </Link>
        </div>
      ) : viewMode === "grid" ? (
        <div className="portfolios-grid">
          {portfolios.map((portfolio) => (
            <PortfolioCard
              key={portfolio.id}
              portfolio={portfolio}
              onDelete={() => handleDeleteClick(portfolio.id)}
            />
          ))}
        </div>
      ) : (
        <PortfolioListView
          portfolios={portfolios}
          onDelete={handleDeleteClick}
        />
      )}



      {/* Content */}
      {/* {loading && portfolios.length === 0 ? (
        <div className="loading">Loading portfolios...</div>
      ) : portfolios.length === 0 ? (
        <div className="no-portfolios">
          <i className="fas fa-briefcase"></i>
          <p>No portfolios found</p>
          <Link to="/app/portfolios/new" className="btn btn-primary">
            Create Your First Portfolio
          </Link>
        </div>
      ) : (
        <div className="portfolios-grid">
          {portfolios.map(portfolio => (
            <PortfolioCard
              key={portfolio.id}
              portfolio={portfolio}
              onDelete={() => handleDeleteClick(portfolio.id)}
            />
          ))}
        </div>
      )} */}
      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Portfolio"
        message="Are you sure you want to delete this portfolio?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedId(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>






  );
};






export default PortfolioList;

